import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { MdVisibility, MdVisibilityOff, MdLock, MdPerson, MdEmail, MdAdminPanelSettings, MdSmartphone } from 'react-icons/md';

export default function Settings() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';

  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [showFields, setShowFields] = useState({ current: false, newPw: false, confirm: false });

  const [appVersionsForm, setAppVersionsForm] = useState({
    android: { version_name: '', version_code: '', force_update: false },
    ios: { version_name: '', version_code: '', force_update: false },
  });
  const [appVersionsLoading, setAppVersionsLoading] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchAppVersions();
    }
  }, [isAdmin]);

  const fetchAppVersions = async () => {
    try {
      const res = await api.get('/admin/settings/app-version');
      if (res.data?.success && res.data?.data) {
        setAppVersionsForm(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch app versions:', err);
    }
  };

  const toggleShow = (field) => setShowFields(prev => ({ ...prev, [field]: !prev[field] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      return toast.error('Passwords do not match');
    }
    if (form.new_password.length < 6) {
      return toast.error('Password must be at least 6 characters');
    }
    setLoading(true);
    try {
      await api.put('/admin/settings/password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success('Password updated successfully');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleAppVersionsSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      return toast.error('Admin access required');
    }
    setAppVersionsLoading(true);
    try {
      await api.post('/admin/settings/app-version', appVersionsForm);
      toast.success('App Version settings updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update app versions');
    } finally {
      setAppVersionsLoading(false);
    }
  };

  const handleAppVersionChange = (platform, field, value) => {
    setAppVersionsForm(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0F172A' }}>Account Settings</h2>
      </div>

      <div className="settings-container">
        <div className={isAdmin ? "settings-grid" : "settings-grid-single"} style={!isAdmin ? { maxWidth: 640, margin: '0 auto' } : {}}>
          
          {/* Main Column */}
          <div className="settings-column-main">
            
            {/* Security Card */}
            <div className="settings-card">
              <div className="settings-card-header">
                <div className="settings-card-title">
                  <MdLock style={{ fontSize: 18, color: '#0F172A' }} /> Security
                </div>
                <div className="settings-card-subtitle">
                  Update your password regularly to keep your account secure.
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="settings-input-group">
                  <label className="settings-input-label">Current Password</label>
                  <div className="settings-input-wrapper">
                    <input 
                      type={showFields.current ? 'text' : 'password'} 
                      value={form.current_password}
                      onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))}
                      placeholder="Enter current password"
                      className="settings-input"
                      required 
                    />
                    <button type="button" className="settings-input-icon-btn" onClick={() => toggleShow('current')} tabIndex={-1}>
                      {showFields.current ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                </div>

                <div className="settings-input-group">
                  <label className="settings-input-label">New Password</label>
                  <div className="settings-input-wrapper">
                    <input 
                      type={showFields.newPw ? 'text' : 'password'} 
                      value={form.new_password}
                      onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))}
                      placeholder="Minimum 6 characters"
                      className="settings-input"
                      required 
                    />
                    <button type="button" className="settings-input-icon-btn" onClick={() => toggleShow('newPw')} tabIndex={-1}>
                      {showFields.newPw ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                </div>

                <div className="settings-input-group">
                  <label className="settings-input-label">Confirm New Password</label>
                  <div className="settings-input-wrapper">
                    <input 
                      type={showFields.confirm ? 'text' : 'password'} 
                      value={form.confirm_password}
                      onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))}
                      placeholder="Confirm your new password"
                      className="settings-input"
                      required 
                    />
                    <button type="button" className="settings-input-icon-btn" onClick={() => toggleShow('confirm')} tabIndex={-1}>
                      {showFields.confirm ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="btn-settings-red" disabled={loading}>
                  {loading ? 'Updating Password...' : 'Update Password'}
                </button>
              </form>
            </div>

            {/* Admin Profile Card */}
            <div className="profile-card">
              <div className="profile-card-banner">
                <div className="profile-avatar-box">
                  {admin?.name?.charAt(0) || 'A'}
                </div>
                <h3 className="profile-title">{admin?.name || 'USER'}</h3>
                <div className="profile-subtitle">{isAdmin ? 'Administrator' : 'Reporter'}</div>
              </div>

              <div className="profile-card-details">
                <div className="profile-detail-item">
                  <span className="profile-detail-label"><MdPerson /> FULL NAME</span>
                  <span className="profile-detail-value">{admin?.name || '-'}</span>
                </div>

                <div className="profile-detail-item">
                  <span className="profile-detail-label"><MdEmail /> EMAIL ADDRESS</span>
                  <span className="profile-detail-value">{admin?.email || '-'}</span>
                </div>

                <div className="profile-detail-item">
                  <span className="profile-detail-label"><MdAdminPanelSettings /> ROLE</span>
                  <span className="profile-role-badge">
                    {isAdmin ? 'Super Admin' : (admin?.role ? admin.role.charAt(0).toUpperCase() + admin.role.slice(1) : 'Reporter')}
                  </span>
                </div>
              </div>
            </div>

            {/* Security Tip Card */}
            <div className="security-tip-card">
              <div className="security-tip-title">
                <MdLock style={{ fontSize: 16 }} /> Security Tip
              </div>
              <p className="security-tip-text">
                Ensure your password uses a mix of uppercase letters, numbers, and symbols for maximum security.
              </p>
            </div>

          </div>

          {/* Right Column — Only rendered for Super Admin */}
          {isAdmin && (
            <div className="settings-column-side">
              
              {/* App Version Settings Card */}
              <div className="settings-card">
                <div className="settings-card-header">
                  <div className="settings-card-title">
                    <MdSmartphone style={{ fontSize: 18, color: '#0F172A' }} /> App Version Settings
                  </div>
                  <div className="settings-card-subtitle">
                    Manage the latest version requirements for Android and iOS mobile apps.
                  </div>
                </div>

                <form onSubmit={handleAppVersionsSubmit}>
                  {/* Android Section */}
                  <div className="app-version-section-header">Android</div>
                  
                  <div className="app-version-grid">
                    <div className="settings-input-group">
                      <label className="settings-input-label">Latest Version Name</label>
                      <input 
                        type="text" 
                        value={appVersionsForm.android.version_name}
                        onChange={(e) => handleAppVersionChange('android', 'version_name', e.target.value)}
                        placeholder="1.0.31"
                        className="settings-input"
                        required
                      />
                    </div>
                    <div className="settings-input-group">
                      <label className="settings-input-label">Latest Version Code</label>
                      <input 
                        type="number" 
                        value={appVersionsForm.android.version_code}
                        onChange={(e) => handleAppVersionChange('android', 'version_code', Number(e.target.value))}
                        placeholder="30"
                        className="settings-input"
                        required
                      />
                    </div>
                  </div>

                  <label className="settings-checkbox-wrapper">
                    <span className="settings-checkbox-label">Force Update</span>
                    <input 
                      type="checkbox" 
                      checked={appVersionsForm.android.force_update}
                      onChange={(e) => handleAppVersionChange('android', 'force_update', e.target.checked)}
                    />
                  </label>

                  {/* iOS Section */}
                  <div className="app-version-section-header">iOS</div>

                  <div className="app-version-grid">
                    <div className="settings-input-group">
                      <label className="settings-input-label">Latest Version Name</label>
                      <input 
                        type="text" 
                        value={appVersionsForm.ios.version_name}
                        onChange={(e) => handleAppVersionChange('ios', 'version_name', e.target.value)}
                        placeholder="1.0.0"
                        className="settings-input"
                        required
                      />
                    </div>
                    <div className="settings-input-group">
                      <label className="settings-input-label">Latest Version Code</label>
                      <input 
                        type="number" 
                        value={appVersionsForm.ios.version_code}
                        onChange={(e) => handleAppVersionChange('ios', 'version_code', Number(e.target.value))}
                        placeholder="1"
                        className="settings-input"
                        required
                      />
                    </div>
                  </div>

                  <label className="settings-checkbox-wrapper">
                    <span className="settings-checkbox-label">Force Update</span>
                    <input 
                      type="checkbox" 
                      checked={appVersionsForm.ios.force_update}
                      onChange={(e) => handleAppVersionChange('ios', 'force_update', e.target.checked)}
                    />
                  </label>

                  <button type="submit" className="btn-settings-red" disabled={appVersionsLoading}>
                    {appVersionsLoading ? 'Saving Settings...' : 'Save Settings'}
                  </button>
                </form>
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
}
